import * as React from "react"
import { Outlet, useLocation } from "react-router-dom"
import { Sidebar } from "./sidebar"
import { Header } from "./header"
import { ErrorBoundary } from "@/components/ui/error-boundary"
import { useAuthStore } from "@/stores/auth.store"
import { ExpenseForm } from "@/components/expenses/expense-form"
import { useGroupCategories } from "@/hooks/queries/useCategories"
import { useGroupUsers } from "@/hooks/queries/useGroups"
import { useActiveGroup } from "@/stores/group.store"
import { useCreateTransaction } from "@/hooks/repository"
import { toast } from "sonner"

import type { Group } from "@/types/domain.types"
import { ExpenseFormProvider, useExpenseForm } from "@/contexts/expense-form.context"

interface AppLayoutProps {
  currentGroup?: Group
  onGroupChange?: (group: Group) => void
}

const AppLayoutInner: React.FC<AppLayoutProps> = ({
  currentGroup,
  onGroupChange: _onGroupChange,
}) => {
  const { user, logout } = useAuthStore()
  const [sidebarOpen, setSidebarOpen] = React.useState(false)
  const legacyOfflineMigrationStarted = React.useRef(false)
  const { isOpen: showExpenseForm, openAddExpenseForm, closeExpenseForm } = useExpenseForm()
  const location = useLocation()

  // Get active group and data for expense form
  const { activeGroupId } = useActiveGroup()
  const { data: categories = [] } = useGroupCategories(activeGroupId || "")
  const { data: groupMembers = [] } = useGroupUsers(activeGroupId || "")
  const { mutateAsync: createTransactionAsync, isLoading: isCreatingExpense } = useCreateTransaction()

  const toggleSidebar = () => setSidebarOpen(!sidebarOpen)

  // Close sidebar when clicking outside on mobile
  React.useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setSidebarOpen(false)
      }
    }

    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  React.useEffect(() => {
    if (!user || legacyOfflineMigrationStarted.current) return

    legacyOfflineMigrationStarted.current = true

    const migrateLegacyOfflineExpenses = async () => {
      const rawLegacyExpenses = localStorage.getItem("offline-expenses")
      if (!rawLegacyExpenses) return

      let legacyExpenses: any[]
      try {
        legacyExpenses = JSON.parse(rawLegacyExpenses)
      } catch {
        return
      }

      if (!Array.isArray(legacyExpenses) || legacyExpenses.length === 0) return

      const migratedIds = new Set<string>()

      for (const legacyExpense of legacyExpenses) {
        if (
          !legacyExpense ||
          legacyExpense.synced ||
          legacyExpense._local?.isDeleted ||
          !legacyExpense.group_id ||
          !legacyExpense.category_id ||
          !legacyExpense.amount
        ) {
          continue
        }

        const createdAt = legacyExpense.created_at || legacyExpense.date || new Date().toISOString()

        try {
          await createTransactionAsync({
            id: `local-migrated-${legacyExpense.id || Date.now()}`,
            type: "expense",
            group_id: legacyExpense.group_id,
            description: legacyExpense.description || "",
            amount: Number(legacyExpense.amount),
            currency_code: legacyExpense.currency_code || "EUR",
            category_id: legacyExpense.category_id,
            paid_by: legacyExpense.paid_by || user.id,
            created_by: legacyExpense.created_by || user.id,
            date: createdAt,
            created_at: createdAt,
            is_synced: false,
            local_changes: true,
          })
          if (legacyExpense.id) migratedIds.add(legacyExpense.id)
        } catch {
          // Keep the legacy row in localStorage so a later sync attempt can recover it.
        }
      }

      if (migratedIds.size === 0) return

      const remaining = legacyExpenses.filter((expense) => !migratedIds.has(expense?.id))
      if (remaining.length > 0) {
        localStorage.setItem("offline-expenses", JSON.stringify(remaining))
      } else {
        localStorage.removeItem("offline-expenses")
      }

      toast.success(`Restored ${migratedIds.size} legacy offline expense${migratedIds.size === 1 ? "" : "s"}`)
    }

    migrateLegacyOfflineExpenses()
  }, [createTransactionAsync, user])

  // Handle expense form submission
  const handleSubmitExpense = async (data: {
    description: string
    amount: number
    currency: string
    date: string
    category_id?: string
    paid_by_user_id: string
    splits: { user_id: string; amount: number; weight?: number }[]
  }) => {
    if (!activeGroupId) {
      toast.error("No active group selected")
      return
    }

    try {
      const category = categories.find((candidate) => candidate.id === data.category_id)
      const paidBy = data.paid_by_user_id || user?.id || ""
      const paidByUser = groupMembers.find((candidate) => candidate.id === paidBy)
      const effectiveDate = data.date ? new Date(data.date).toISOString() : new Date().toISOString()

      await createTransactionAsync({
        id: `local-${Date.now()}`,
        type: "expense",
        group_id: activeGroupId,
        description: data.description,
        amount: data.amount,
        currency_code: data.currency,
        category_id: data.category_id || "",
        paid_by: paidBy,
        created_by: user?.id,
        date: effectiveDate,
        created_at: effectiveDate,
        is_synced: false,
        local_changes: true,
        user: paidByUser ? {
          id: paidByUser.id,
          name: paidByUser.name,
          email: paidByUser.email,
        } : undefined,
        category: category ? {
          id: category.id,
          name: category.name,
          emoji: category.emoji,
          pinned: category.pinned,
        } : undefined,
      })
      closeExpenseForm()
    } catch (error) {
      toast.error("Failed to add expense")
    }
  }

  // Check if we're on the expenses page (which has its own ExpenseForm)
  const isExpensesPage = location.pathname === '/expenses'

  if (!user) {
    return null // This should be handled by the auth guard
  }

  return (
    <div className="flex h-screen bg-background">
      {/* Sidebar */}
      <Sidebar
        user={user}
        currentGroup={currentGroup}
        isOpen={sidebarOpen}
        onToggle={toggleSidebar}
        onLogout={logout}
      />

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0 relative">
        {/* Header - Sticky on mobile */}
        <div className="sticky top-0 z-30 md:relative md:z-auto">
          <Header
            user={user}
            currentGroup={currentGroup}
            onMenuToggle={toggleSidebar}
            showSearch={true}
            showAddButton={true}
            onAddExpense={openAddExpenseForm}
          />
        </div>

        {/* Page Content */}
        <main className="flex-1 overflow-auto">
          <div className="container mx-auto p-0 pt-4 md:p-6 lg:p-8">
            <ErrorBoundary>
              <Outlet />
            </ErrorBoundary>
          </div>
        </main>
      </div>

      {/* Global Expense Form - only render if not on expenses page (which has its own) */}
      {!isExpensesPage && (
        <ExpenseForm
          open={showExpenseForm}
          onOpenChange={(open) => {
            if (!open) {
              closeExpenseForm()
            }
          }}
          categories={categories}
          groupMembers={groupMembers}
          currentUserId={user?.id || ""}
          onSubmit={handleSubmitExpense}
          isSubmitting={isCreatingExpense}
        />
      )}
    </div>
  )
}

export const AppLayout: React.FC<AppLayoutProps> = (props) => {
  return (
    <ExpenseFormProvider>
      <AppLayoutInner {...props} />
    </ExpenseFormProvider>
  )
}

// Layout for authentication pages
export const AuthLayout: React.FC = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-background to-muted/20 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-2 mb-2">
            <div className="h-8 w-8 bg-primary rounded-lg flex items-center justify-center">
              <span className="text-primary-foreground font-bold">M</span>
            </div>
            <span className="text-2xl font-bold">Moitie</span>
          </div>
          <p className="text-muted-foreground">
            Track and split expenses with your group
          </p>
        </div>
        <ErrorBoundary>
          <Outlet />
        </ErrorBoundary>
      </div>
    </div>
  )
}

// Simple layout for public pages
export const PublicLayout: React.FC = () => {
  return (
    <div className="min-h-screen bg-background">
      <header className="border-b">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center gap-2">
            <div className="h-6 w-6 bg-primary rounded flex items-center justify-center">
              <span className="text-primary-foreground font-bold text-sm">M</span>
            </div>
            <span className="font-bold text-lg">Moitie</span>
          </div>
        </div>
      </header>
      <main>
        <ErrorBoundary>
          <Outlet />
        </ErrorBoundary>
      </main>
    </div>
  )
}
